import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Loader2, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface MeasurementImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  tradeId?: string;
  tradeCode: string;
  orgId: string;
  onSuccess: () => void;
}

interface UploadedReview {
  parsed: any;
  storagePath: string;
  fileName: string;
}

export function MeasurementImportDialog({
  open,
  onOpenChange,
  jobId,
  tradeId,
  tradeCode,
  orgId,
  onSuccess,
}: MeasurementImportDialogProps) {
  const [uploading, setUploading] = useState(false);
  const [review, setReview] = useState<UploadedReview | null>(null);
  const [openingPdf, setOpeningPdf] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setReview(null);
    setUploading(false);
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      toast.error("Please upload a PDF file");
      event.target.value = "";
      return;
    }

    setUploading(true);

    try {
      // 1. Upload PDF to storage so the user can preview it later
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const storagePath = `${orgId}/${jobId}/measurements/${Date.now()}-${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from("job-documents")
        .upload(storagePath, file, { contentType: "application/pdf", upsert: false });
      if (uploadError) throw uploadError;

      // 2. Read base64 and parse
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const { data, error } = await supabase.functions.invoke("parse-measurement-report", {
        body: {
          file_data: base64,
          file_name: file.name,
          job_id: jobId,
          trade_id: tradeId,
          trade_code: tradeCode,
          org_id: orgId,
          storage_path: storagePath,
        },
      });

      if (error) throw error;

      toast.success("Parsed! Review the values below before closing.");
      setReview({
        parsed: data?.parsed ?? {},
        storagePath,
        fileName: file.name,
      });
      onSuccess();
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload measurement report");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleOpenOriginalPdf = async () => {
    if (!review) return;
    setOpeningPdf(true);
    try {
      const { data, error } = await supabase.storage
        .from("job-documents")
        .createSignedUrl(review.storagePath, 60 * 10);
      if (error) throw error;
      const url = (data as any)?.signedUrl ?? (data as any)?.signedURL;
      if (!url) throw new Error("Could not generate preview URL");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      toast.error(e.message || "Could not open the PDF");
    } finally {
      setOpeningPdf(false);
    }
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const p = review?.parsed ?? {};

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{review ? "Verify Measurement Report" : "Import Measurement Report"}</DialogTitle>
          <DialogDescription>
            {review
              ? "Confirm these values match the PDF you uploaded."
              : "Upload a measurement report from Hover, EagleView, GAF, Roofr, or other services"}
          </DialogDescription>
        </DialogHeader>

        {!review ? (
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-primary" />
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-1">Upload PDF Report</h3>
                <p className="text-sm text-muted-foreground">
                  Supports Hover, EagleView, GAF Roof Hub, Roofr, and other measurement PDFs
                </p>
              </div>

              <Button type="button" variant="outline" disabled={uploading} onClick={openFilePicker}>
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Choose PDF File
                  </>
                )}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 text-sm text-emerald-600">
              <CheckCircle2 className="w-4 h-4" />
              Parsed <span className="font-medium truncate">{review.fileName}</span>
            </div>

            <div className="rounded-lg border bg-muted/30 p-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Row label="Provider" value={p.provider} />
              <Row label="Total Squares" value={p.total_squares} suffix=" SQ" />
              <Row label="Total Area" value={p.total_area_sqft?.toLocaleString?.()} suffix=" sq ft" />
              <Row label="Pitch" value={p.predominant_pitch} />
              <Row label="Ridges" value={p.ridges_lf ?? p.combined_hip_ridge_lf} suffix=" LF" />
              <Row label="Hips" value={p.hips_lf} suffix=" LF" />
              <Row label="Valleys" value={p.valleys_lf} suffix=" LF" />
              <Row label="Eaves" value={p.eaves_lf} suffix=" LF" />
              <Row label="Rakes" value={p.rakes_lf} suffix=" LF" />
              <Row label="Facets" value={p.number_of_facets} />
              <Row label="Stories" value={p.stories} />
              <Row label="Waste %" value={p.waste_factor_percent} suffix="%" />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
              <Button variant="outline" onClick={handleOpenOriginalPdf} disabled={openingPdf}>
                {openingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Open Uploaded PDF
              </Button>
              <Button onClick={() => handleClose(false)}>Looks good</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, suffix }: { label: string; value: any; suffix?: string }) {
  const display = value === null || value === undefined || value === "" ? "—" : `${value}${suffix ?? ""}`;
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{display}</span>
    </div>
  );
}
